import React, { useState, useEffect, useMemo } from "react";
import { Globe, ChevronDown, ChevronUp, Play } from "lucide-react";
import {
  extractDomain,
  isPotentialYouTubeURL,
  fetchBatchURLMetadata,
} from "../../utils/urlMetadata";
import { getDefaultTitleFromUrl } from "../../utils/helpers";
import logger from "../../services/Logger";

// Utility function to format duration for display
const formatDuration = (duration) => {
  if (!duration) return "";
  if (duration.includes(":")) return duration;
  const seconds = parseInt(duration, 10);
  if (isNaN(seconds)) return "";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

const CitationCard = ({ source, index, prefetchedMetadata = null }) => {
  const [metadata, setMetadata] = useState(prefetchedMetadata);
  const [loading, setLoading] = useState(!prefetchedMetadata);
  const [faviconError, setFaviconError] = useState(false);

  const sourceUrl =
    source?.web?.url || source?.web?.uri || source?.url || source?.uri;
  const sourceTitle = source?.web?.title || source?.title;

  const defaultTitle = getDefaultTitleFromUrl(sourceUrl, sourceTitle, `Source ${index + 1}`);

  useEffect(() => {
    // If we already have prefetched metadata, use it and skip fetching
    if (prefetchedMetadata) {
      setMetadata(prefetchedMetadata);
      setLoading(false);
      return;
    }

    // Don't fetch metadata from frontend - this should come from backend
    const domain = extractDomain(sourceUrl);
    const fallbackMetadata = {
      title: sourceTitle || defaultTitle,
      domain: domain || "Unknown domain",
      favicon: null, // Only show favicons when provided by backend via prefetchedMetadata
      realUrl: sourceUrl,
    };

    setMetadata(fallbackMetadata);
    setLoading(false);
  }, [sourceUrl, sourceTitle, index, prefetchedMetadata, defaultTitle]);

  if (!sourceUrl) return null;

  // Check if this is a YouTube video
  const isYouTube = isPotentialYouTubeURL(sourceUrl, sourceTitle);

  return (
    <a
      href={metadata?.realUrl || sourceUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`relative p-[10px] border rounded-[10px] flex flex-col transition-colors duration-200 ${
        isYouTube
          ? "bg-fill-hex-e8ecf480 dark:bg-fill-hex-252629 border-edge-border dark:border-edge-strong-border hover:bg-fill-sidebar-action-surface dark:hover:bg-fill-hex-2a2a2d"
          : "bg-fill-hex-e8ecf480 dark:bg-fill-hex-252629 border-edge-border dark:border-edge-strong-border hover:bg-fill-sidebar-action-surface dark:hover:bg-fill-hex-2a2a2d"
      }`}
    >
      {/* Citation number badge */}
      <span className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] px-[3px] bg-fill-blue-100 dark:bg-fill-blue-900/60 text-ink-blue-600 dark:text-ink-blue-400 text-[9px] font-semibold rounded flex items-center justify-center leading-none">
        {index + 1}
      </span>
      <div className="flex-grow min-h-[28px] pr-4">
        <h4
          className={`text-[11px] line-clamp-2 font-medium ${
            isYouTube
              ? "text-ink-ink dark:text-ink-white"
              : "text-ink-ink dark:text-ink-white"
          }`}
        >
          {metadata?.title || defaultTitle}
        </h4>
        {/* Duration display for YouTube videos */}
        {isYouTube && metadata?.duration && (
          <span className="text-ink-gray-600 dark:text-ink-gray-400 text-[9px] font-medium mt-1 inline-block">
            {formatDuration(metadata.duration)}
          </span>
        )}
      </div>
      <div className="mt-[10px] flex items-center gap-1">
        <div className="w-3 h-3 flex items-center justify-center">
          {isYouTube ? (
            <div className="w-3 h-3 bg-fill-red-600 rounded-sm flex items-center justify-center">
              <Play className="w-1.5 h-1.5 text-ink-white ml-0.5" />
            </div>
          ) : metadata?.favicon && !faviconError ? (
            <img
              src={metadata.favicon}
              alt={`${metadata.domain} favicon`}
              className="w-3 h-3 rounded"
              onError={() => setFaviconError(true)}
              onLoad={() => setFaviconError(false)}
            />
          ) : (
            <Globe className="w-3 h-3 text-ink-gray-400 dark:text-ink-gray-500" />
          )}
        </div>
        <p
          className={`text-[10px] ${
            isYouTube
              ? "text-ink-hex-0d31487d dark:text-ink-hex-aaaaaa"
              : "text-ink-hex-0d31487d dark:text-ink-hex-aaaaaa"
          }`}
        >
          {metadata?.domain ||
            (loading
              ? "Loading domain..."
              : extractDomain(sourceUrl) || "Unknown domain")}
        </p>
        {/* Author for YouTube videos */}
        {isYouTube && metadata?.author && (
          <>
            <span className="text-ink-gray-400 dark:text-ink-gray-500 text-[10px]">
              •
            </span>
            <span className="text-ink-gray-600 dark:text-ink-gray-400 text-[9px] truncate">
              {metadata.author}
            </span>
          </>
        )}
      </div>
    </a>
  );
};

const CitationsPreview = ({ sources, messageId }) => {
  const [showAllSources, setShowAllSources] = useState(false);
  const [batchMetadata, setBatchMetadata] = useState({});

  // Memoize validSources array to stabilize reference for deps
  const validSources = useMemo(
    () =>
      sources?.filter((source) => {
        const url =
          source?.web?.url ||
          source?.web?.uri ||
          source?.url ||
          source?.uri;
        return url && (url.startsWith("http://") || url.startsWith("https://"));
      }) || [],
    [sources]
  );

  // Fetch batch metadata for performance when there are multiple sources
  useEffect(() => {
    const fetchBatchMetadata = async () => {
      if (validSources.length <= 1) {
        // For single source or no sources, let individual components handle it
        return;
      }

      try {
        // Extract URLs for batch processing
        const urls = validSources
          .map(
            (source) =>
              source?.web?.url ||
              source?.web?.uri ||
              source?.url ||
              source?.uri
          )
          .filter(Boolean);

        if (urls.length > 1) {
          // Check for redirect info requirement (Vertex AI)
          const includeRedirectInfo = urls.some((url) =>
            url.includes("vertexaisearch.cloud.google.com")
          );

          const batchResults = await fetchBatchURLMetadata(
            urls,
            includeRedirectInfo
          );

          // Map URLs to their fetched metadata
          const metadataMap = {};
          urls.forEach((url, index) => {
            if (batchResults[index]) {
              metadataMap[url] = batchResults[index];
            }
          });

          setBatchMetadata(metadataMap);
        }
      } catch (error) {
        logger.warn('CitationsPreview', 'Failed to fetch batch metadata for sources', { error: error.message });
      }
    };

    fetchBatchMetadata();
    // ONLY use validSources in deps, so effect is only triggered when truly needed
  }, [validSources]);

  // Show top 4 sources with expansion for remainder
  const topSources = validSources.slice(0, 4);
  const remainingSources = validSources.slice(4);
  const hasMoreSources = validSources.length > 4;

  if (validSources.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 space-y-6">
      {/* Sources Section */}
      <div
        data-sources-section={messageId || 'default'}
        className="pt-6 border-t-2 border-edge-blue-100 dark:border-edge-blue-900/25 bg-gradient-to-r from-gradient-blue-50/50 to-gradient-indigo-50/50 dark:from-gradient-hex-172554/10 dark:to-gradient-hex-1e1b4b/10 rounded-xl p-5"
      >
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-ink-gray-800 dark:text-ink-gray-200 flex items-center gap-2.5">
            <div className="p-1.5 bg-fill-blue-100 dark:bg-fill-blue-900/50 rounded-lg">
              <Globe className="w-3.5 h-3.5 text-ink-blue-600 dark:text-ink-blue-400" />
            </div>
            Sources ({validSources.length})
            <div className="h-1 w-1 rounded-full bg-fill-gray-400 dark:bg-fill-gray-500"></div>
            <span className="text-xs font-normal text-ink-gray-600 dark:text-ink-gray-400">
              Referenced in this answer
            </span>
          </h4>
        </div>

        {/* Always show top 4 sources */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {topSources.map((source, index) => {
            const sourceUrl =
              source?.web?.url ||
              source?.web?.uri ||
              source?.url ||
              source?.uri;
            const prefetchedMetadata = batchMetadata[sourceUrl] || null;

            return (
              <CitationCard
                key={index}
                source={source}
                index={index}
                prefetchedMetadata={prefetchedMetadata}
              />
            );
          })}
        </div>

        {/* Expandable section for remaining sources */}
        {hasMoreSources && (
          <div className="mt-3">
            <button
              onClick={() => setShowAllSources((prev) => !prev)}
              className="flex items-center gap-2 text-xs font-medium text-ink-blue-600 dark:text-ink-blue-400 hover:text-ink-blue-800 dark:hover:text-ink-blue-300 transition-colors duration-200 mb-3"
            >
              {showAllSources ? (
                <>
                  <ChevronUp className="w-3 h-3" />
                  Show fewer sources
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" />
                  View {remainingSources.length} more source
                  {remainingSources.length !== 1 ? "s" : ""}
                </>
              )}
            </button>

            {/* Animated expansion for remaining sources */}
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                showAllSources
                  ? "max-h-[2000px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 pt-2 border-t border-edge-blue-200 dark:border-edge-blue-900/40">
                {remainingSources.map((source, index) => {
                  const sourceUrl =
                    source?.web?.url ||
                    source?.web?.uri ||
                    source?.url ||
                    source?.uri;
                  const prefetchedMetadata = batchMetadata[sourceUrl] || null;

                  return (
                    <CitationCard
                      key={index + 4} // Offset key to avoid conflicts
                      source={source}
                      index={index + 4}
                      prefetchedMetadata={prefetchedMetadata}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CitationsPreview;
